import * as React from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * Context for sharing connection status with child components
 */
export interface IConnectionStatus {
  isOnline: boolean;
}

export const ConnectionStatusContext =
  React.createContext<IConnectionStatus | null>(null);

/**
 * Hook to access connection status from any child component
 */
export const useConnectionStatus = (): IConnectionStatus => {
  const context = React.useContext(ConnectionStatusContext);
  if (!context) {
    throw new Error(
      'useConnectionStatus must be used within ConnectionStatusWrapper'
    );
  }
  return context;
};

/**
 * Props for the ConnectionStatusWrapper component
 */
export interface IConnectionStatusWrapperProps {
  children: React.ReactNode;
}

/**
 * ConnectionStatusWrapper component - Monitors network connection status
 * Displays a banner when offline and provides connection status to child components
 */
export const ConnectionStatusWrapper: React.FC<
  IConnectionStatusWrapperProps
> = ({ children }) => {
  const [isOnline, setIsOnline] = React.useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  React.useEffect(() => {
    // Handler for when connection is restored
    const handleOnline = (): void => {
      setIsOnline(true);
    };

    // Handler for when connection is lost
    const handleOffline = (): void => {
      setIsOnline(false);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return (): void => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const contextValue = React.useMemo(
    (): IConnectionStatus => ({
      isOnline,
    }),
    [isOnline]
  );

  return (
    <ConnectionStatusContext.Provider value={contextValue}>
      <OfflineBanner $isVisible={!isOnline}>
        <BannerContent>
          <BannerIcon>☁️</BannerIcon>
          <BannerText>No internet connection</BannerText>
        </BannerContent>
      </OfflineBanner>
      {children}
    </ConnectionStatusContext.Provider>
  );
};

// Animations

const slideDown = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-100%);
    opacity: 0;
  }
`;

// Styled Components

const OfflineBanner = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: ${(props) => (props.$isVisible ? '40px' : '0px')};
  background-color: #dc3545;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;
  transition: height 300ms ease-in-out;
  animation: ${(props) => (props.$isVisible ? slideDown : slideUp)} 300ms
    ease-in-out;
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BannerIcon = styled.span`
  font-size: 16px;
  filter: grayscale(100%);
`;

const BannerText = styled.span`
  font-size: 14px;
  font-weight: 500;
`;
